import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { saveReturnUrl, savePendingIntent } from "@/lib/postLoginRedirect";
import {
  PlayCircle, Calendar, BadgeCheck, Users, Clock, MapPin, Video,
  Radio, ArrowRight, Loader2, ChevronRight, ShieldCheck,
} from "lucide-react";

const fontStack = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };
const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };
const TRAINER_IMG = "https://customer-assets.emergentagent.com/job_digital-scale-cwk/artifacts/ayfsnv1k_1%20%282%29.png";

export default function LiveList() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | online | offline | today

  useEffect(() => {
    Promise.all([
      api.get("/classes").then(({ data }) => setClasses(data || [])).catch(() => {}),
      api.get("/batches").then(({ data }) => setBatches(data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const now = Date.now();
  const sevenDaysOut = now + 7 * 86400000;
  const todayStr = new Date().toDateString();

  // Group classes by batch_id and keep only those within next 7 days (or live now)
  const groupedBatches = useMemo(() => {
    const activeBatches = batches.filter((b) => {
      if (b.status === "active") return true;
      const s = new Date(b.start_date).getTime();
      const e = new Date(b.end_date).getTime();
      return s <= now && now <= e;
    });
    let result = activeBatches.map((b) => {
      const batchClasses = classes
        .filter((c) => {
          const t = new Date(c.scheduled_at).getTime();
          const endT = t + (c.duration_minutes || 60) * 60 * 1000;
          const inWindow = t <= sevenDaysOut && endT >= now;
          if (!inWindow || c.batch_id !== b.id) return false;
          if (filter === "today" && new Date(c.scheduled_at).toDateString() !== todayStr) return false;
          return true;
        })
        .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
      const liveNow = batchClasses.filter((c) => {
        const t = new Date(c.scheduled_at).getTime();
        return t <= now && now <= t + (c.duration_minutes || 60) * 60 * 1000;
      });
      return { batch: b, classes: batchClasses, liveNow };
    });
    // Apply mode filter
    if (filter === "online") result = result.filter((g) => (g.batch.mode || "online") !== "offline");
    if (filter === "offline") result = result.filter((g) => g.batch.mode === "offline" || g.batch.mode === "hybrid");
    if (filter === "today") result = result.filter((g) => g.classes.length > 0);
    return result;
  }, [batches, classes, now, sevenDaysOut, filter, todayStr]);

  const totalUpcoming = groupedBatches.reduce((sum, g) => sum + g.classes.length, 0);
  const totalLiveNow = groupedBatches.reduce((sum, g) => sum + g.liveNow.length, 0);

  // Counts for chips (unfiltered)
  const baseGroups = useMemo(() => {
    const ab = batches.filter((b) => {
      if (b.status === "active") return true;
      const s = new Date(b.start_date).getTime();
      const e = new Date(b.end_date).getTime();
      return s <= now && now <= e;
    });
    const todayClasses = classes.filter((c) => {
      const t = new Date(c.scheduled_at);
      return t.toDateString() === todayStr && ab.some((b) => b.id === c.batch_id);
    });
    return {
      all: ab.length,
      online: ab.filter((b) => (b.mode || "online") !== "offline").length,
      offline: ab.filter((b) => b.mode === "offline" || b.mode === "hybrid").length,
      today: todayClasses.length,
    };
  }, [batches, classes, now, todayStr]);

  const FILTERS = [
    { key: "all", label: "All batches", icon: Radio, count: baseGroups.all },
    { key: "online", label: "Online", icon: Video, count: baseGroups.online, color: "pink" },
    { key: "offline", label: "Offline", icon: MapPin, count: baseGroups.offline, color: "emerald" },
    { key: "today", label: "Today's classes", icon: Calendar, count: baseGroups.today, color: "amber" },
  ];

  return (
    <div className="bg-[#050505] text-white min-h-screen" style={fontStack}>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-[#FF0066]/15 rounded-full blur-[160px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-semibold uppercase tracking-[0.14em] mb-5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
            {totalLiveNow > 0 ? `${totalLiveNow} live right now` : "Live batches"}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight" style={displayStack}>
            Your <span className="text-[#FF0066]">live batches</span> & this week's classes
          </h1>
          <p className="mt-4 text-base sm:text-lg text-white/70 max-w-2xl">
            All running batches shown below — online + offline. Each batch shows only the next 7 days of scheduled classes for clarity.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
            <Pill icon={Radio} value={groupedBatches.length} label="Active batches" />
            <Pill icon={Calendar} value={totalUpcoming} label="Classes (7 days)" />
            <Pill icon={PlayCircle} value={totalLiveNow} label="Live now" color={totalLiveNow > 0 ? "red" : "default"} />
          </div>
        </div>
      </section>

      {/* FILTER CHIPS */}
      <section className="sticky top-16 z-30 bg-[#050505]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center gap-2 overflow-x-auto" data-testid="live-filter-chips">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            const ActiveBg = active
              ? f.color === "emerald" ? "bg-emerald-500 text-white border-emerald-500"
                : f.color === "amber" ? "bg-amber-500 text-black border-amber-500"
                : "bg-[#FF0066] text-white border-[#FF0066]"
              : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10";
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border transition ${ActiveBg}`}
                data-testid={`filter-${f.key}`}
              >
                <f.icon className="w-3.5 h-3.5" />
                <span>{f.label}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${active ? "bg-black/25" : "bg-white/10 text-white/60"}`}>{f.count}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {loading && (
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-16 text-center">
            <Loader2 className="w-8 h-8 text-[#FF0066] mx-auto animate-spin" />
            <div className="mt-3 text-sm text-white/60">Loading batches…</div>
          </div>
        )}

        {!loading && groupedBatches.length === 0 && (
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-12 text-center">
            <Radio className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <div className="text-xl font-bold text-white" style={displayStack}>
              {filter === "today" ? "No classes scheduled today"
                : filter === "online" ? "No online batches running"
                : filter === "offline" ? "No offline batches running"
                : "No live batches running"}
            </div>
            <p className="text-sm text-white/60 mt-2 max-w-md mx-auto">
              {filter !== "all" ? "Try switching the filter, or" : "Currently no batches are active."} new batches start every month — check back soon or get WhatsApp alerts.
            </p>
            <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
              {filter !== "all" && <button onClick={() => setFilter("all")} className="inline-flex items-center gap-2 bg-white/5 border border-white/15 hover:bg-white/10 text-white font-medium rounded-full px-5 py-2.5 text-sm transition">Show all batches</button>}
              <Link to="/courses" className="inline-flex items-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white font-medium rounded-full px-5 py-2.5 text-sm transition">
                Browse courses <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {groupedBatches.map(({ batch, classes: bClasses, liveNow }) => (
            <BatchSection key={batch.id} batch={batch} classes={bClasses} liveNow={liveNow} user={user} />
          ))}
        </div>
      </section>
    </div>
  );
}

function Pill({ icon: Icon, value, label, color }) {
  const bg = color === "red" ? "bg-red-500/10 border-red-500/30" : "bg-white/[0.03] border-white/10";
  const ic = color === "red" ? "text-red-400" : "text-[#FF0066]";
  return (
    <div className={`border ${bg} rounded-xl px-3 py-2.5`}>
      <Icon className={`w-4 h-4 ${ic}`} />
      <div className="text-xl font-bold text-white mt-1" style={displayStack}>{value}</div>
      <div className="text-[10px] text-white/50 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function BatchSection({ batch, classes, liveNow, user }) {
  const isOffline = batch.mode === "offline";
  const isHybrid = batch.mode === "hybrid";
  const modeBadge = isOffline
    ? { txt: "Offline", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" }
    : isHybrid
    ? { txt: "Hybrid", cls: "bg-blue-500/20 text-blue-300 border-blue-500/40" }
    : { txt: "Online", cls: "bg-[#FF0066]/20 text-[#ff6699] border-[#FF0066]/40" };

  return (
    <div className="bg-[#121212] border border-white/10 rounded-2xl overflow-hidden">
      {/* Batch header */}
      <div className="p-5 sm:p-6 bg-gradient-to-r from-[#FF0066]/10 via-[#1a1a1a] to-[#0a0a0a] border-b border-white/5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider ${modeBadge.cls}`}>
                {modeBadge.txt}
              </span>
              {liveNow.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/20 border border-red-500/40 text-[10px] uppercase tracking-wider text-red-300 font-bold">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-80" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                  </span>
                  Live now
                </span>
              )}
              <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Batch</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white" style={displayStack}>{batch.name}</div>
            <div className="text-xs text-white/60 mt-1">{batch.course_title}</div>

            <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1.5 text-xs text-white/70">
              {batch.timing && <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#FF0066]" /> {batch.timing}</span>}
              {isOffline && batch.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-emerald-400" /> {batch.location.split(",").slice(0, 2).join(",")}</span>}
              {!isOffline && batch.meeting_provider && <span className="inline-flex items-center gap-1.5"><Video className="w-3.5 h-3.5 text-[#FF0066]" /> {batch.meeting_provider}</span>}
              <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-white/50" /> {batch.enrolled_count || 0}/{batch.capacity || 0}</span>
            </div>
          </div>

          {/* Trainer badge */}
          <div className="inline-flex items-center gap-2 bg-black/40 border border-white/10 rounded-full pl-1 pr-3 py-1 shrink-0">
            <img src={TRAINER_IMG} alt={batch.teacher_name || "Kamlesh Singad"} className="w-7 h-7 rounded-full object-cover border border-[#FF0066]/40" />
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Trainer</span>
              <span className="text-xs font-semibold text-white">{batch.teacher_name || "Kamlesh Singad"}</span>
              <ShieldCheck className="w-3 h-3 text-[#FF0066]" />
            </div>
          </div>
        </div>
      </div>

      {/* Classes list */}
      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs uppercase tracking-[0.16em] text-white/50 font-semibold">Next 7 days · {classes.length} {classes.length === 1 ? "class" : "classes"}</div>
          {classes.length === 0 && <span className="text-[10px] text-white/40 uppercase">All classes recorded — check dashboard</span>}
        </div>

        {classes.length === 0 ? (
          <div className="bg-[#0a0a0a] border border-white/5 rounded-lg p-6 text-center">
            <Calendar className="w-8 h-8 text-white/15 mx-auto mb-2" />
            <div className="text-sm text-white/60">No classes in the next 7 days</div>
            <div className="text-xs text-white/40 mt-1">Recordings of recent classes available in your student dashboard.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {classes.map((c) => {
              const t = new Date(c.scheduled_at);
              const startMs = t.getTime();
              const isLive = startMs <= Date.now() && Date.now() <= startMs + (c.duration_minutes || 60) * 60 * 1000;
              const isToday = t.toDateString() === new Date().toDateString();
              return (
                <ClassRow key={c.id} cls={c} dt={t} isLive={isLive} isToday={isToday} isOffline={isOffline} batchLocation={batch.location} user={user} />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ClassRow({ cls, dt, isLive, isToday, isOffline, batchLocation, user }) {
  const dayName = dt.toLocaleDateString("en-IN", { weekday: "short" });
  const dayNum = dt.getDate();
  const month = dt.toLocaleDateString("en-IN", { month: "short" });
  const time = dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border transition ${isLive ? "bg-red-500/10 border-red-500/40" : "bg-[#0a0a0a] border-white/5 hover:border-[#FF0066]/30"}`}>
      {/* Date pill */}
      <div className={`shrink-0 w-14 text-center rounded-md py-1.5 px-1 ${isLive ? "bg-red-500 text-white" : isToday ? "bg-[#FF0066] text-white" : "bg-[#121212] border border-white/10 text-white/80"}`}>
        <div className="text-[9px] uppercase tracking-wider font-bold">{dayName}</div>
        <div className="text-lg font-extrabold leading-none my-0.5" style={displayStack}>{dayNum}</div>
        <div className="text-[9px] uppercase tracking-wider">{month}</div>
      </div>

      {/* Class info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-semibold text-white truncate">{cls.title}</div>
          {isLive && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500 text-white text-[9px] uppercase tracking-wider font-bold">
              <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> Live
            </span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-3 text-[11px] text-white/50">
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {time} · {cls.duration_minutes || 60}m</span>
          {cls.teacher_name && <span className="inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3 text-[#FF0066]" /> {cls.teacher_name}</span>}
          {isOffline ? (
            batchLocation && <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3 text-emerald-400" /> {batchLocation.split(",")[0]}</span>
          ) : (
            <span className="inline-flex items-center gap-1"><Video className="w-3 h-3 text-[#FF0066]" /> Online</span>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="shrink-0">
        {isOffline ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 font-bold">
            <MapPin className="w-3 h-3" /> In-centre
          </span>
        ) : isLive ? (
          user ? (
            <Link to={`/live/${cls.id}`} className="inline-flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-full px-3.5 py-1.5 text-xs transition" data-testid={`join-live-${cls.id}`}>
              <PlayCircle className="w-3.5 h-3.5" /> Join
            </Link>
          ) : (
            <Link
              to="/login"
              onClick={() => {
                saveReturnUrl(`/live/${cls.id}`);
                savePendingIntent({ type: "join_live", class_id: cls.id });
              }}
              className="text-xs text-[#FF0066] hover:underline"
            >Login to join</Link>
          )
        ) : (
          user ? (
            <Link to={`/live/${cls.id}`} className="inline-flex items-center gap-1 text-xs text-[#FF0066] hover:text-[#ff3380] font-medium">
              View <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          ) : null
        )}
      </div>
    </div>
  );
}
